(() => {
  const rootSelector = '[data-dm-visible-product-autoplay]';
  const carouselSelector = '[data-dm-viewport-autoplay]';
  const interactionSelector = carouselSelector + ', .dm-mobile-snap-arrows';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new Map();

  const getInterval = (root) => {
    const interval = Number(root.dataset.dmAutoplayInterval);
    return Number.isFinite(interval) && interval >= 2000 ? interval : 7000;
  };

  const getActiveCarousel = (root) => {
    const activeTabCarousel = root.querySelector('.tab-content.active ' + carouselSelector);
    return activeTabCarousel || root.querySelector(carouselSelector);
  };

  const getDirectProducts = (carousel) => Array.from(carousel.children).filter((item) => (
    item.classList.contains('product')
  ));

  const getSnapInset = (carousel) => (
    carousel instanceof Element
      ? parseFloat(window.getComputedStyle(carousel).scrollPaddingLeft) || 0
      : 0
  );

  const advanceMobileCarousel = (carousel) => {
    if (!(carousel instanceof Element)) return false;
    const products = getDirectProducts(carousel);
    if (products.length < 2) return false;

    const snapInset = getSnapInset(carousel);
    const target = carousel.scrollLeft + snapInset;
    const activeIndex = products.reduce((bestIndex, product, index) => (
      Math.abs(product.offsetLeft - target) < Math.abs(products[bestIndex].offsetLeft - target)
        ? index
        : bestIndex
    ), 0);
    const nextProduct = products[activeIndex + 1];
    if (!nextProduct) return false;

    carousel.scrollTo({
      left: Math.max(0, nextProduct.offsetLeft - snapInset),
      behavior: 'smooth'
    });
    return true;
  };

  const getSlick = (carousel) => {
    if (!window.jQuery?.fn?.slick || !carousel.classList.contains('slick-initialized')) return null;
    try {
      return window.jQuery(carousel).slick('getSlick');
    } catch (error) {
      return null;
    }
  };

  const advanceSlickCarousel = (carousel) => {
    const slick = getSlick(carousel);
    if (!slick || slick.unslicked) return false;

    slick.pause?.();
    const slidesToShow = Math.max(1, Math.ceil(Number(slick.options.slidesToShow) || 1));
    const slidesToScroll = Math.max(1, Number(slick.options.slidesToScroll) || 1);
    const lastStart = Math.max(0, slick.slideCount - slidesToShow);
    const currentSlide = Math.max(0, Number(slick.currentSlide) || 0);
    const nextSlide = Math.min(lastStart, currentSlide + slidesToScroll);
    if (nextSlide <= currentSlide) return false;

    slick.slideHandler(nextSlide);
    return true;
  };

  class VisibleProductAutoplay {
    constructor(root) {
      this.root = root;
      this.interval = getInterval(root);
      this.timer = null;
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      this.destroyed = false;
      this.handlePointerOver = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        const interactionArea = event.target.closest?.(interactionSelector);
        if (!interactionArea || interactionArea.contains(event.relatedTarget)) return;
        this.hovered = true;
        this.sync();
      };
      this.handlePointerOut = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        const interactionArea = event.target.closest?.(interactionSelector);
        if (!interactionArea || interactionArea.contains(event.relatedTarget)) return;
        this.hovered = false;
        this.sync(true);
      };
      this.handleFocusIn = (event) => {
        if (!event.target.closest?.(interactionSelector)) return;
        this.focused = true;
        this.sync();
      };
      this.handleFocusOut = (event) => {
        if (event.relatedTarget?.closest?.(interactionSelector)) return;
        this.focused = false;
        this.sync(true);
      };
      this.handleVisibilityChange = () => this.sync(true);
      this.handleMotionChange = () => this.sync(true);
      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.visible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.2);
        this.sync(true);
      }, { threshold: [0, 0.2] });
    }

    mount() {
      this.root.addEventListener('pointerover', this.handlePointerOver);
      this.root.addEventListener('pointerout', this.handlePointerOut);
      this.root.addEventListener('focusin', this.handleFocusIn);
      this.root.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      reducedMotion.addEventListener?.('change', this.handleMotionChange);
      this.observer.observe(this.root);
      this.pauseNativeAutoplay();
    }

    canRun() {
      return this.visible
        && !this.hovered
        && !this.focused
        && !document.hidden
        && !reducedMotion.matches;
    }

    pauseNativeAutoplay() {
      this.root.querySelectorAll(carouselSelector).forEach((carousel) => {
        getSlick(carousel)?.pause?.();
      });
    }

    clearTimer() {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    sync(restart = false) {
      if (this.destroyed) return;
      this.pauseNativeAutoplay();
      if (restart) this.clearTimer();
      if (!this.canRun()) {
        this.clearTimer();
        return;
      }
      if (this.timer === null) {
        this.timer = window.setTimeout(() => this.tick(), this.interval);
      }
    }

    tick() {
      this.timer = null;
      if (!this.canRun()) return;

      const carousel = getActiveCarousel(this.root);
      if (carousel) {
        if (carousel.classList.contains('dm-mobile-snap-carousel')) {
          advanceMobileCarousel(carousel);
        } else {
          advanceSlickCarousel(carousel);
        }
      }
      this.sync();
    }

    destroy() {
      this.destroyed = true;
      this.clearTimer();
      this.observer.disconnect();
      this.root.removeEventListener('pointerover', this.handlePointerOver);
      this.root.removeEventListener('pointerout', this.handlePointerOut);
      this.root.removeEventListener('focusin', this.handleFocusIn);
      this.root.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      reducedMotion.removeEventListener?.('change', this.handleMotionChange);
    }
  }

  const mount = (scope = document) => {
    const roots = [];
    if (scope.matches?.(rootSelector)) roots.push(scope);
    scope.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (controllers.has(root)) return;
      const controller = new VisibleProductAutoplay(root);
      controllers.set(root, controller);
      controller.mount();
    });
  };

  const unmount = (scope) => {
    Array.from(controllers.entries()).forEach(([root, controller]) => {
      if (root === scope || scope.contains?.(root)) {
        controller.destroy();
        controllers.delete(root);
      }
    });
  };

  const start = () => mount();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
})();
